import React from 'react';

interface Tech {
  name: string;
  icon: string;
  color: string;
  category: 'frontend' | 'backend' | 'database' | 'devops';
}

const TECH_STACK: Tech[] = [
  { name: 'React', icon: '⚛️', color: '#61DAFB', category: 'frontend' },
  { name: 'Next.js', icon: '▲', color: '#ffffff', category: 'frontend' },
  { name: 'TypeScript', icon: 'TS', color: '#3178C6', category: 'frontend' },
  { name: 'Angular', icon: '🅰', color: '#DD0031', category: 'frontend' },
  { name: 'Node.js', icon: '⬡', color: '#339933', category: 'backend' },
  { name: 'Express', icon: '∞', color: '#ffffff', category: 'backend' },
  { name: 'PHP', icon: 'PHP', color: '#777BB4', category: 'backend' },
  { name: 'REST APIs', icon: '⇄', color: '#10b981', category: 'backend' },
  { name: 'MongoDB', icon: '🍃', color: '#47A248', category: 'database' },
  { name: 'MySQL', icon: '🐬', color: '#4479A1', category: 'database' },
  { name: 'Redis', icon: '⬟', color: '#DC382D', category: 'database' },
  { name: 'AWS', icon: '☁', color: '#FF9900', category: 'devops' },
  { name: 'Docker', icon: '🐳', color: '#2496ED', category: 'devops' },
  { name: 'Git', icon: '⎇', color: '#F05032', category: 'devops' },
];

const CATEGORY_LABELS: Record<Tech['category'], string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  database: 'Database',
  devops: 'DevOps & Cloud',
};

const CATEGORIES: Tech['category'][] = ['frontend', 'backend', 'database', 'devops'];

const TechStack: React.FC = () => {
  return (
    <section id="skills" className="py-28 bg-[#0d0d14]">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">
            Arsenal
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white">Tech Stack</h2>
          <p className="text-slate-400 mt-3 max-w-xl">
            Technologies I use to build, scale, and ship production-grade applications.
          </p>
        </div>

        {/* Grid by category */}
        <div className="space-y-12">
          {CATEGORIES.map((cat) => {
            const items = TECH_STACK.filter((t) => t.category === cat);
            return (
              <div key={cat}>
                <h3 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2">
                  {CATEGORY_LABELS[cat]}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {items.map((tech) => (
                    <div
                      key={tech.name}
                      className="group flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-[#13131e] border border-slate-800 hover:border-slate-600 transition-all duration-200 cursor-default"
                    >
                      <span
                        className="text-lg leading-none font-bold w-5 text-center"
                        style={{ color: tech.color }}
                      >
                        {tech.icon}
                      </span>
                      <span className="text-sm font-semibold text-slate-300 group-hover:text-white transition-colors">
                        {tech.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Proficiency bars */}
        <div className="mt-20 grid md:grid-cols-2 gap-8">
          {[
            { label: 'Frontend / UI Engineering', pct: 90, color: '#10b981' },
            { label: 'Backend / API Architecture', pct: 85, color: '#06b6d4' },
            { label: 'Database Design & Optimization', pct: 80, color: '#8b5cf6' },
            { label: 'DevOps / Cloud Infrastructure', pct: 70, color: '#f59e0b' },
          ].map((bar) => (
            <div key={bar.label}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-slate-300">{bar.label}</span>
                <span className="text-sm font-mono text-slate-500">{bar.pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${bar.pct}%`,
                    background: `linear-gradient(90deg, ${bar.color}cc, ${bar.color})`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
